import { useState, useEffect, useRef } from 'react';
import { IconRefresh, IconEdit, IconBan, IconLoader2, IconX, IconAlertTriangle } from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

/**
 * DEV-1953 (FR013) — the scheduler's queue view. Every campaign that ever
 * went through `POST /campaigns/multi-channel/send` with a `scheduledAt`,
 * newest scheduled time first.
 *
 * EDIT SCOPE, DELIBERATELY NARROW: the backend's `PUT /campaigns/scheduled/:id`
 * is a full-replace (name + channels + content + scheduledAt + audience),
 * because it re-runs the complete Phase-1 gate exactly like a fresh schedule
 * call. Rebuilding CampaignsPage's entire multi-channel composer inside this
 * dialog (SMS/Inbox/Push/Email variant editors, activity-filter preview,
 * etc.) was out of scope for this PR. This dialog therefore only exposes
 * "Campaign Name" and "Release time" as editable, and round-trips the
 * fetched channels/content/audience UNCHANGED for everything else — an
 * operator who needs to change message content cancels and re-schedules
 * instead. This is a real, deliberate limitation, not an oversight.
 */

type ScheduledCampaignStatus = 'SCHEDULED' | 'SENDING' | 'SENT' | 'CANCELLED' | 'FAILED';

interface ScheduledCampaign {
  id: string;
  mvnoId: string;
  name: string;
  channels: string[];
  status: ScheduledCampaignStatus;
  scheduledAt: string | null;
  scheduledBy: string | null;
  releasedAt: string | null;
  lateRelease: boolean;
  failureReason: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  scheduledRecipientEstimate: number | null;
  releaseStuck: boolean;
  /** Codex fix C17 — a SCHEDULED row past `scheduled_at` that the dispatcher
   * hasn't claimed yet (auth misconfig, dispatcher down, Cloud Scheduler
   * paused). Unlike `releaseStuck` (claimed but never concluded), this row
   * was never even picked up — silent otherwise, since it just sits there
   * looking identical to a normal future SCHEDULED row. */
  overdue: boolean;
}

const getApiErrorMessage = (err: any, fallback: string): string => (
  err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback
);

/** SAST display — every scheduled/released/cancelled timestamp on this page
 * is shown in Africa/Johannesburg, matching what the operator typed and what
 * the WASPA guard actually enforces, never the browser's own zone. */
function formatSast(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg' }) + ' SAST';
}

function StatusBadge({ status }: { status: ScheduledCampaignStatus }) {
  switch (status) {
    case 'SENT':
      return <Badge variant="success">{status}</Badge>;
    case 'FAILED':
      return <Badge variant="destructive">{status}</Badge>;
    case 'CANCELLED':
      return <Badge variant="warning">{status}</Badge>;
    case 'SCHEDULED':
    case 'SENDING':
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

const EDITABLE_STATUSES: ScheduledCampaignStatus[] = ['SCHEDULED'];

/**
 * Codex fix C21(b). The server's real cutoff (`CAMPAIGN_SCHEDULE_CANCEL_CUTOFF_MINUTES`,
 * default 5) is the authority — this is a client-side ESTIMATE, only as
 * fresh as this row's last load, so it can never be tightened into a real
 * guarantee. Its purpose is narrower: stop the confirm dialog from ever
 * asserting "nothing will be sent" and then having the PUT/cancel 400 a
 * few seconds later purely because time passed while the operator was
 * looking at the table.
 */
const CLIENT_CANCEL_CUTOFF_MINUTES = 5;

function isEditableNow(campaign: ScheduledCampaign): boolean {
  if (!EDITABLE_STATUSES.includes(campaign.status) || !campaign.scheduledAt) {
    return false;
  }
  const msUntilRelease = new Date(campaign.scheduledAt).getTime() - Date.now();
  return msUntilRelease > CLIENT_CANCEL_CUTOFF_MINUTES * 60_000;
}

/** "YYYY-MM-DDTHH:mm" for a `<input type="datetime-local">`, from an ISO
 * instant, rendered in SAST — the inverse of CampaignsPage's
 * `scheduledAtIso` (which appends the fixed `+02:00` offset back on submit). */
function toSastDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/** Same instant, as a datetime-local `min` — Codex fix C21(a). */
function toSastDatetimeLocalMin(at: Date): string {
  return toSastDatetimeLocal(at.toISOString());
}

function EditScheduledCampaignDialog({
  target,
  onClose,
  onSaved,
}: {
  target: ScheduledCampaign | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [scheduledAtLocal, setScheduledAtLocal] = useState('');
  // The rest of the full-replace body, round-tripped verbatim — see the
  // file header for why this dialog never lets an operator touch it.
  const [detail, setDetail] = useState<{
    mvnoId: string;
    channels: string[];
    content: Record<string, unknown>;
    activeWithinDays: number | null;
    scheduledRecipientEstimate: number | null;
  } | null>(null);
  // Codex fix C6 (nuance): `detail` alone isn't enough — it must be paired
  // with WHICH campaign it was fetched for. Without this, campaign B's GET
  // failing (network blip, 404 race) left campaign A's `detail` rendering
  // under B's name/dialog with Save still enabled (`!detail` was false),
  // so Save would PUT A's brand+content onto B's id. `detailForId` is
  // compared against `target.id` everywhere `detail` is used to decide
  // whether to render/allow saving.
  const [detailForId, setDetailForId] = useState<string | null>(null);
  // Codex fix C6: a request-generation token, not just a loading flag. A
  // slow response for campaign A resolving AFTER the dialog has already
  // been reopened for campaign B must not overwrite B's dialog with A's
  // content — every state-applying callback below checks its own captured
  // `requestId` against the ref's CURRENT value first, and applies nothing
  // if a newer target has since superseded it.
  const requestIdRef = useRef(0);

  /** Codex fix C6 (nuance): this dialog is mounted ONCE for the page (no
   * `key` per target), so closing it must explicitly clear every field —
   * otherwise the NEXT target's dialog briefly renders the PREVIOUS
   * target's stale name/schedule/detail/error before its own fetch lands. */
  const resetState = () => {
    setLoading(false);
    setSaving(false);
    setError('');
    setName('');
    setScheduledAtLocal('');
    setDetail(null);
    setDetailForId(null);
  };

  useEffect(() => {
    if (!target) {
      resetState();
      return;
    }
    const requestId = (requestIdRef.current += 1);
    setLoading(true);
    setError('');
    // Clear immediately, not just on the next successful fetch — a fast
    // reopen must never render the PREVIOUS target's fetched content even
    // for an instant.
    setDetail(null);
    setDetailForId(null);
    setName(target.name);
    setScheduledAtLocal(toSastDatetimeLocal(target.scheduledAt));
    api.get(`/campaigns/multi-channel/${encodeURIComponent(target.id)}`)
      .then((response) => {
        if (requestId !== requestIdRef.current) return; // a newer open owns the dialog — a stale response must not overwrite its state
        const campaign = response.data.campaign;
        setDetail({
          mvnoId: campaign.mvnoId,
          channels: campaign.channels || [],
          content: campaign.content || {},
          activeWithinDays: campaign.audience?.activeWithinDays ?? null,
          scheduledRecipientEstimate: target.scheduledRecipientEstimate,
        });
        setDetailForId(target.id);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return; // a newer open owns the dialog — a stale response must not overwrite its state
        setError(getApiErrorMessage(err, 'Failed to load campaign detail'));
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return; // a newer open owns the dialog — a stale response must not overwrite its state
        setLoading(false);
      });
  }, [target]);

  // Codex fix C6 (nuance): the ONE guard that matters for Save — `detail`
  // must exist AND must have been fetched for THIS target, not a
  // now-superseded one.
  const detailIsCurrent = !!detail && detailForId === target?.id;

  const handleSave = async () => {
    if (!target || !detailIsCurrent || !detail) return;
    if (!name.trim()) { setError('Campaign name is required'); return; }
    if (!scheduledAtLocal) { setError('Release time is required'); return; }
    setSaving(true);
    setError('');
    try {
      await api.put(`/campaigns/scheduled/${encodeURIComponent(target.id)}`, {
        campaignName: name.trim(),
        mvnoId: detail.mvnoId,
        // NOT a swap: the edit input's `channels` field is the per-channel
        // CONTENT map ({ sms?, inbox?, push?, email? }) — the same shape
        // sendMultiChannel takes — while the catalogue row stores that map
        // as `content` and uses `channels` for the bare channel-name list.
        channels: detail.content,
        activeWithinDays: detail.activeWithinDays,
        scheduledAt: `${scheduledAtLocal}:00+02:00`,
      });
      onSaved(`"${name.trim()}" updated.`);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to update the scheduled campaign'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    resetState();
    onClose();
  };

  return (
    <Dialog open={!!target} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Scheduled Campaign</DialogTitle>
          <DialogDescription>
            Rename or reschedule. Message content and channels are unchanged — cancel and
            re-schedule instead if those need to change.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center p-6">
            <IconLoader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {!loading && detailIsCurrent && detail && (
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="edit-name">Campaign Name</Label>
              <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} disabled={saving} />
            </div>
            <div>
              <Label htmlFor="edit-scheduled-at">Release time — SAST (Africa/Johannesburg)</Label>
              <Input
                id="edit-scheduled-at"
                type="datetime-local"
                value={scheduledAtLocal}
                onChange={(e) => setScheduledAtLocal(e.target.value)}
                disabled={saving}
                // Codex fix C21(a)/(d).
                min={toSastDatetimeLocalMin(new Date(Date.now() + 15 * 60_000))}
                step={60}
              />
            </div>
            <div className="text-xs text-muted-foreground">
              Channels: {detail.channels.join(', ') || '—'}. Both the lead-time and WASPA
              marketing-hours checks re-run against the new time on save.
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading || !detailIsCurrent}>
            {saving ? <IconLoader2 size={16} className="animate-spin" /> : null}
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelScheduledCampaignDialog({
  target,
  cancelling,
  cancelError,
  onConfirm,
  onClose,
}: {
  target: ScheduledCampaign | null;
  cancelling: boolean;
  cancelError: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!target} onOpenChange={(open) => { if (!open && !cancelling) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel Scheduled Campaign</DialogTitle>
          <DialogDescription>
            "{target?.name}" will not be released. Nothing has been sent yet, and nothing will be —
            this cannot be undone once confirmed.
          </DialogDescription>
        </DialogHeader>
        {cancelError && (
          <Alert variant="destructive">
            <AlertDescription>{cancelError}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={cancelling}>Keep Scheduled</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={cancelling}>
            {cancelling ? <IconLoader2 size={16} className="animate-spin" /> : <IconBan size={16} />}
            {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Codex fix C19 — the recovery path for a FAILED campaign that no longer has
 * a NEW-NAME trap: reuses the same id/audit trail instead of a fresh
 * `sendMultiChannel` schedule call under a new name (which would start a
 * brand-new reservation namespace with no dedupe against the failed
 * attempt). Eligibility (status=FAILED AND zero rows in either job table) is
 * enforced ATOMICALLY server-side inside the UPDATE's WHERE clause — this
 * button is shown for every FAILED row rather than pre-filtering client-side
 * (the queue view doesn't carry job counts), and a genuinely-ineligible row
 * (partially dispatched before failing) surfaces its rejection as a 400 in
 * this dialog rather than silently graying out the button.
 */
function RescheduleFailedCampaignDialog({
  target,
  onClose,
  onRescheduled,
}: {
  target: ScheduledCampaign | null;
  onClose: () => void;
  onRescheduled: (message: string) => void;
}) {
  const [scheduledAtLocal, setScheduledAtLocal] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!target) {
      setScheduledAtLocal('');
      setSaving(false);
      setError('');
      return;
    }
    setScheduledAtLocal('');
    setSaving(false);
    setError('');
  }, [target]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleReschedule = async () => {
    if (!target) return;
    if (!scheduledAtLocal) { setError('Release time is required'); return; }
    setSaving(true);
    setError('');
    try {
      await api.post(`/campaigns/scheduled/${encodeURIComponent(target.id)}/reschedule`, {
        scheduledAt: `${scheduledAtLocal}:00+02:00`,
      });
      onRescheduled(`"${target.name}" rescheduled.`);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to reschedule this campaign'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reschedule Failed Campaign</DialogTitle>
          <DialogDescription>
            "{target?.name}" failed before releasing. Pick a new release time — content, channels
            and audience are unchanged and re-validated (lead time, horizon, WASPA hours) against
            the new time. Only allowed while zero jobs exist for this campaign; a partially
            dispatched attempt is rejected here.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="reschedule-at">New release time — SAST (Africa/Johannesburg)</Label>
          <Input
            id="reschedule-at"
            type="datetime-local"
            value={scheduledAtLocal}
            onChange={(e) => setScheduledAtLocal(e.target.value)}
            disabled={saving}
            min={toSastDatetimeLocalMin(new Date(Date.now() + 15 * 60_000))}
            step={60}
          />
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleReschedule} disabled={saving}>
            {saving ? <IconLoader2 size={16} className="animate-spin" /> : null}
            {saving ? 'Rescheduling...' : 'Reschedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ScheduledCampaignsPage() {
  const [campaigns, setCampaigns] = useState<ScheduledCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editTarget, setEditTarget] = useState<ScheduledCampaign | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ScheduledCampaign | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [rescheduleTarget, setRescheduleTarget] = useState<ScheduledCampaign | null>(null);

  const loadCampaigns = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/campaigns/scheduled');
      setCampaigns(response.data.campaigns || []);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load scheduled campaigns'));
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCampaigns(); }, []);

  const closeCancelDialog = () => { setCancelTarget(null); setCancelError(''); };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    setCancelError('');
    try {
      await api.post(`/campaigns/scheduled/${encodeURIComponent(cancelTarget.id)}/cancel`);
      setSuccess(`Cancelled "${cancelTarget.name}".`);
      closeCancelDialog();
      await loadCampaigns();
    } catch (err: any) {
      setCancelError(getApiErrorMessage(err, 'Failed to cancel the scheduled campaign'));
    } finally {
      setCancelling(false);
    }
  };

  const handleEditSaved = async (message: string) => {
    setEditTarget(null);
    setSuccess(message);
    setError('');
    await loadCampaigns();
  };

  const handleRescheduled = async (message: string) => {
    setRescheduleTarget(null);
    setSuccess(message);
    setError('');
    await loadCampaigns();
  };

  return (
    <div>
      <PageHeader
        title="Scheduled Campaigns"
        subtitle="Campaigns queued for a future release, and their outcome once the dispatcher runs."
      />

      <div className="flex items-center gap-4 mb-4">
        <Button onClick={loadCampaigns} disabled={loading} variant="outline">
          <IconRefresh className="mr-2 h-4 w-4" />
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <Button variant="ghost" size="icon-xs" onClick={() => setError('')}><IconX size={14} /></Button>
          </AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {success}
            <Button variant="ghost" size="icon-xs" onClick={() => setSuccess('')}><IconX size={14} /></Button>
          </AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="flex justify-center p-8">
          <IconLoader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {!loading && campaigns.length > 0 && (
        <MainCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold">Name</TableHead>
                <TableHead className="font-bold">Brand</TableHead>
                <TableHead className="font-bold">Channels</TableHead>
                <TableHead className="font-bold">Status</TableHead>
                <TableHead className="font-bold">Release Time (SAST)</TableHead>
                <TableHead className="font-bold">Scheduled By</TableHead>
                <TableHead className="font-bold">Est. Recipients</TableHead>
                <TableHead className="font-bold">Notes</TableHead>
                <TableHead className="font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow
                  key={campaign.id}
                  className={cn((campaign.releaseStuck || campaign.overdue) && 'bg-destructive/10')}
                >
                  <TableCell className="font-medium">{campaign.name}</TableCell>
                  <TableCell>{campaign.mvnoId}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {campaign.channels.map((channel) => (
                        <Badge key={channel} variant="outline">{channel}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <StatusBadge status={campaign.status} />
                      {campaign.lateRelease && <Badge variant="warning">Late release</Badge>}
                      {campaign.releaseStuck && (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <IconAlertTriangle size={12} /> Release stuck
                        </Badge>
                      )}
                      {campaign.overdue && (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <IconAlertTriangle size={12} /> Overdue — not claimed
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{formatSast(campaign.scheduledAt)}</TableCell>
                  <TableCell className="text-xs">{campaign.scheduledBy || '—'}</TableCell>
                  <TableCell>{campaign.scheduledRecipientEstimate ?? '—'}</TableCell>
                  <TableCell className="text-xs max-w-xs">
                    {campaign.status === 'FAILED' && campaign.failureReason && (
                      <span className="text-destructive">{campaign.failureReason}</span>
                    )}
                    {campaign.status === 'CANCELLED' && (
                      <span className="text-muted-foreground">
                        by {campaign.cancelledBy || 'unknown'} at {formatSast(campaign.cancelledAt)}
                      </span>
                    )}
                    {campaign.releaseStuck && (
                      <span className="text-destructive">
                        Claimed {formatSast(campaign.releasedAt)} but never concluded — needs an
                        operator; this is never auto-retried.
                      </span>
                    )}
                    {campaign.overdue && (
                      <span className="text-destructive">
                        Past its release time and not yet claimed by the dispatcher — check the
                        Cloud Scheduler job and dispatch-due auth; this is never auto-retried.
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!isEditableNow(campaign)}
                        title={
                          !isEditableNow(campaign)
                            ? 'Not editable — either not SCHEDULED, or too close to its release time'
                            : undefined
                        }
                        onClick={() => setEditTarget(campaign)}
                      >
                        <IconEdit size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive"
                        disabled={!isEditableNow(campaign)}
                        title={
                          !isEditableNow(campaign)
                            ? 'Not cancellable — either not SCHEDULED, or too close to its release time'
                            : undefined
                        }
                        onClick={() => { setCancelTarget(campaign); setCancelError(''); }}
                      >
                        <IconBan size={14} />
                      </Button>
                      {campaign.status === 'FAILED' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRescheduleTarget(campaign)}
                        >
                          Reschedule
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </MainCard>
      )}

      {!loading && campaigns.length === 0 && !error && (
        <MainCard className="text-center">
          <p className="text-muted-foreground">
            No campaign has ever been scheduled yet. Use "Schedule" instead of "Send now" on the
            Campaigns page to queue one for a future release.
          </p>
        </MainCard>
      )}

      <EditScheduledCampaignDialog
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={handleEditSaved}
      />
      <CancelScheduledCampaignDialog
        target={cancelTarget}
        cancelling={cancelling}
        cancelError={cancelError}
        onConfirm={handleConfirmCancel}
        onClose={closeCancelDialog}
      />
      <RescheduleFailedCampaignDialog
        target={rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        onRescheduled={handleRescheduled}
      />
    </div>
  );
}
